import React, { useContext } from 'react';
import { motion } from 'framer-motion';
import { SubOptions } from '../../../lib/models/interfaces';
import { UserContext } from '../../../context/authContext';

interface OptionsProps {
  setShowOptions: (arg: boolean) => void;
  setShowPayment: (arg: boolean) => void;
  setSelectedOption: (arg: SubOptions) => void;
  // setOptionSeletceted(arg: string) => void;
}
// Types for stronger TS safety
interface Plan {
  key: 'free' | 'pGo' | 'base' | 'mid' | 'max' | 'enterprise';
  name:
    | 'Free Trial'
    | 'Single User'
    | 'Project'
    | 'Business'
    | 'Enterprise'
    | 'Pay As You Go';
  price: string;
  cadence: string;
  blurb: string;
  bullets: string[];
  description: string;
  priceId: SubOptions;
}

// Options data (drives the UI)
const options: Plan[] = [
  {
    key: 'pGo',
    name: 'Pay As You Go',
    price: '$ 9.99',
    cadence: 'per month',
    blurb: 'Pay for what you need.',
    bullets: [
      'Limited Projects',
      '1 User',
      'Email support',
      '100 Credits',
      '~ 145 Pages Analyzed',
      '~ 80 RFP Questions',
      '~ 20 Searches'
    ],
    description:
      'Great for individuals who only what to pay for what they use.',
    priceId: SubOptions.pGo
  },
  {
    key: 'base',
    name: 'Single User',
    price: '$ 49',
    cadence: 'per month',
    blurb: 'Essentials to get started',
    bullets: [
      'Unlimited Projects',
      '1 User',
      'Email support',
      '800 Credits',
      '~ 1100 Pages Analyzed',
      '~ 650 RFP Questions',
      '~ 200 Searches',
      'Purchase More Credits'
    ],
    description:
      'Great for individuals that need the essentials without overhead.',
    priceId: SubOptions.sUser
  },
  {
    key: 'mid',
    name: 'Project',
    price: '$ 99',
    cadence: 'per month',
    blurb: 'Small Teams',
    bullets: [
      'Unlimited Projects',
      '3 Users',
      'Email support',
      '2000 Credits',
      '~ 2800 Pages Analyzed',
      '~ 1600 RFP Questions',
      '~ 500 Searches',
      'Purchase More Credits'
    ],
    description: 'Great for Small Teams.',
    priceId: SubOptions.project
  },
  {
    key: 'max',
    name: 'Business',
    price: '$ 190',
    cadence: 'per month',
    blurb: 'Larger Teams',
    bullets: [
      'Unlimited Projects',
      '7 Users',
      'Email support',
      '4200 Credits',
      '~ 5700 Pages Analyzed',
      '~ 3300 RFP Questions',
      '~ 1050 Searches',
      'Purchase More Credits'
    ],
    description: 'Larger teams that need more processing capacity.',
    priceId: SubOptions.business
  }
  // {
  //   key: 'enterprise',
  //   name: 'Enterprise',
  //   price: 'Contact Sales',
  //   cadence: '',
  //   blurb: 'Enterprise Wide Use',
  //   bullets: ['Unlimited Projects', '', '', '', ''],
  //   description:
  //     'Designed for large organizations that require advanced controls, security, and guaranteed response times.',
  //   priceId: SubOptions.max
  // }
];

const freeTrial: Plan[] = [
  {
    key: 'free',
    name: 'Free Trial',
    price: '7 days Free',
    cadence: '',
    blurb: 'Converts to Pay as You Go',
    bullets: [
      'Free for 7 Days',
      '~ 145 Pages analyzed',
      '~ 83 RFP Questions',
      'Cancel anytime'
    ],
    description: 'Risk free testing.',
    priceId: SubOptions.pGoT
  }
];

const SelectOptionsPage: React.FC<OptionsProps> = ({
  setSelectedOption,
  setShowOptions,
  setShowPayment
}) => {
  const { user } = useContext(UserContext);
  const PriceCard = (props: { opt: Plan }) => {
    const { opt } = props;
    return (
      <div
        key={opt.key}
        className="rounded-2xl border border-black/5 dark:border-white/10 bg-white/80 dark:bg-slate-900/70 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.4)] flex flex-col min-h-[460px]"
      >
        <div className="flex h-full flex-col">
          {/* Price area */}
          <div className="px-6 pt-6 pb-3 ">
            <div className="flex items-baseline gap-2">
              <span className="text-3xl font-semibold text-slate-900 dark:text-white">
                {opt.price}
              </span>
              <span className="text-sm text-slate-500 dark:text-slate-400">
                {opt.cadence}
              </span>
            </div>
            <div className="mt-1 text-sm text-slate-700 dark:text-slate-300">
              {opt.blurb}
            </div>
          </div>

          {/* Heading & bullets */}
          <div className="px-6 pb-4 pt-1">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
              {opt.name}
            </h2>
            <ul className="min-h-[200px] mt-4 space-y-2 rounded-xl  p-4 text-xs leading-relaxed text-slate-900">
              {opt.bullets.filter(Boolean).map((b, i) => (
                <li
                  key={i}
                  className="relative pl-5 text-left before:absolute before:left-0 before:top-2 before:h-1.5 before:w-1.5 before:rounded-full before:bg-slate-900"
                >
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* Spacer to push CTA/description to align */}
          <div className="mt-auto px-6 pb-6">
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowOptions(false);
                  setShowPayment(true);
                  setSelectedOption(opt.priceId);
                }}
                className="mx-6 mb-4 rounded-lg bg-[#62d29e] px-4 py-2 text-white font-medium shadow hover:bg-[#4fb88a] transition"
              >
                Select
              </button>
            </div>
            <p className="mt-6 min-h-[84px] border-t border-white/10 pt-4 text-sm">
              {opt.description}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // main return
  return (
    <div className="h-screen overscroll-auto overflow-scroll w-full bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-[#62d29e]/40 via-[#62d29e]/20 to-white dark:from-slate-950 dark:via-slate-950 dark:to-black flex items-center justify-center p-6">
      {/* Subtle grid overlay */}
      <div className="pointer-events-none fixed inset-0 opacity-[0.04] [background-image:linear-gradient(to_right,theme(colors.slate.500/.35)_1px,transparent_1px),linear-gradient(to_bottom,theme(colors.slate.500/.35)_1px,transparent_1px)] [background-size:32px_32px]" />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35 }}
        className="relative z-10 w-full max-w-7xl"
      >
        {/* Page header */}
        <div className="mb-6 text-center">
          <img
            src="/korulogo.png"
            alt="Koru Logo"
            className="mx-auto mb-4 h-12 w-12 object-contain"
            loading="eager"
            decoding="async"
          />
          <h1 className="text-3xl font-semibold text-slate-900 dark:text-white">
            Choose Your Plan
          </h1>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Pick the option that fits where you are today—upgrade anytime.
          </p>
        </div>

        {/* Cards */}
        <div
          className={`grid grid-cols-1 gap-6  items-stretch ${user?.freeTrialKey ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}
        >
          {user.freeTrialKey && freeTrial.map((opt) => <PriceCard opt={opt} />)}
          {options.map((opt) => (
        
            <PriceCard opt={opt} />
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default SelectOptionsPage;
